/* eslint-disable frontApplet/enforce-jsdoc */
//send {variable} into the object so we can output its name in warning
import AppletResourcesError from '../Error/AppletResourcesError';
import ErrorCodes from '../Error/ErrorCodes';
import ErrorCodesMessages from '../Error/ErrorCodesMessages';
import ValidateObjectInterface from './ValidateObjectInterface';

interface IOutsideValidationSubject {
	[key: string]: any;
}

export interface IValidationSubject {
	name: string;
	subject: any;
}

export const validatableType = ['string', 'number', 'boolean', 'function', 'object', 'undefined', 'null', 'array'] as const;
export type ValidatableType = (typeof validatableType)[number];

export class Validate {
	private validationSubject: IValidationSubject;
	private error: Error = undefined!;
	private _canBeNull: boolean = false;

	public constructor(validationSubject: IOutsideValidationSubject) {
		this.validationSubject = this.getSubjectFromOutside(validationSubject);
	}

	public static oneOfPossibleTypesIsType(types: ValidatableType | ValidatableType[], type: ValidatableType) {
		if (!Array.isArray(types)) {
			types = [types];
		}
		for (const t of types) {
			if (t === type) {
				return true;
			}
		}
		return false;
	}

	public static validateType(types: ValidatableType | ValidatableType[], subject: any) {
		if (!Array.isArray(types)) {
			types = [types];
		}
		let outputType = '';
		for (const t of types) {
			const valid = this.validateSingleType(t, subject);
			if (valid === true) {
				return true;
			} else {
				outputType = valid;
			}
		}
		return outputType;
	}

	public static validateSingleType(type: ValidatableType, subject: any) {
		return typeof subject === type ? true : typeof subject;
	}

	public canBeNull() {
		this._canBeNull = true;
		return this;
	}

	public setError(error: Error) {
		this.error = error;
		return this;
	}

	public required(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject === 'undefined') {
			this.throwError('is required', errorCode);
		}
		return this;
	}

	public boolean(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject !== 'boolean') {
			this.throwError(`has to be boolean. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		}
		return this;
	}

	public number(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject !== 'number') {
			this.throwError(`has to be number. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		}
		return this;
	}

	public min(value: number, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject === 'number') {
			if (this.validationSubject.subject < value) {
				this.throwError(`has to be at least ${value}. Got ${this.validationSubject.subject}`, errorCode);
			}
		}
		return this;
	}

	public max(value: number, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject === 'number') {
			if (this.validationSubject.subject > value) {
				this.throwError(`has to be at least ${value}. Got ${this.validationSubject.subject}`, errorCode);
			}
		}
		return this;
	}

	public string(equals?: (string | undefined)[] | null, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject !== 'string') {
			this.throwError(`has to be string. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		} else if (Array.isArray(equals) && !equals.includes(this.validationSubject.subject)) {
			this.throwError(`has to be one of ${equals}. Got ${this.validationSubject.subject} instead`, errorCode);
		}
		return this;
	}

	public lengthMin(value: number, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && this.validationSubject.subject?.length < value) {
			this.throwError(`can't be shorter than ${value}`, errorCode);
		}
		return this;
	}

	public lengthMax(value: number, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && this.validationSubject.subject?.length > value) {
			this.throwError(`can't be longer than ${value}`, errorCode);
		}
		return this;
	}

	public object(objectInterface?: object, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (
			typeof this.validationSubject.subject !== 'undefined' &&
			(typeof this.validationSubject.subject !== 'object' || this.validationSubject.subject === null)
		) {
			this.throwError(`has to be object. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		} else if (typeof objectInterface !== 'undefined') {
			this.checkObjectInterface(objectInterface, this.validationSubject.subject);
		}
		return this;
	}

	public array(types: ValidatableType | ValidatableType[], objectInterface?: object, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && !Array.isArray(this.validationSubject.subject)) {
			this.throwError(`has to be array. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		} else {
			for (const content of this.validationSubject.subject ?? []) {
				const valid = Validate.validateType(types, content);
				if (valid !== true) {
					this.throwError(`has to be array of types: ${JSON.stringify(types)}. ${JSON.stringify(content)} is ${valid} instead`, errorCode);
				}

				if (typeof objectInterface !== 'undefined') {
					if (Validate.oneOfPossibleTypesIsType(types, 'object')) {
						this.checkObjectInterface(objectInterface, content);
					}
				}
			}
		}
		return this;
	}

	public function(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && typeof this.validationSubject.subject !== 'function') {
			this.throwError(`has to be function. Got ${typeof this.validationSubject.subject} instead`, errorCode);
		}
		return this;
	}

	public date(errorCode?: ErrorCodes) {
		function isValidDate(date: Date) {
			return date && Object.prototype.toString.call(date) === '[object Date]';
		}

		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && !isValidDate(this.validationSubject.subject)) {
			this.throwError(`has to be instance of Date and be valid`, errorCode);
		}
		return this;
	}

	public uri(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		}
		//regex found here https://stackoverflow.com/questions/30847/regex-to-validate-uris
		const regex = new RegExp(
			"^([a-z0-9+.-]+):(?://(?:((?:[a-z0-9-._~!$&'()*+,;=:]|%[0-9A-F]{2})*)@)?((?:[a-z0-9-._~!$&'()*+,;=]|%" +
				"[0-9A-F]{2})*)(?::(\\d*))?(/(?:[a-z0-9-._~!$&'()*+,;=:@/]|%[0-9A-F]{2})*)?|(/?(?:[a-z0-9-._~!$&'()*+,;=:@]|%[0-9A-F]{2})+" +
				"(?:[a-z0-9-._~!$&'()*+,;=:@/]|%[0-9A-F]{2})*)?)(?:\\?((?:[a-z0-9-._~!$&'()*+,;=:/?@]|%[0-9A-F]{2})*))?" +
				"(?:#((?:[a-z0-9-._~!$&'()*+,;=:/?@]|%[0-9A-F]{2})*))?$",
			'i',
		);
		if (typeof this.validationSubject.subject !== 'string') {
			this.throwError(`has to be string. Got ${typeof this.validationSubject.subject} instead`, errorCode);
			return this;
		}
		if (!this.validationSubject.subject.match(regex)) {
			this.throwError(`has to be valid URI.`, errorCode);
		}
		return this;
	}

	public matchRegExp(regexp: RegExp, errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject !== 'undefined' && !regexp.test(this.validationSubject.subject)) {
			this.throwError(`contains invalid characters. Valid pattern: ${regexp.toString()}`, errorCode);
		}
		return this;
	}

	public timerTime(errorCode?: ErrorCodes) {
		if (this.canBeNullAndIs()) {
			return this;
		} else if (typeof this.validationSubject.subject === 'string') {
			const timeArray = this.validationSubject.subject.split(':');
			let max = 23;
			for (const time of timeArray) {
				if (parseInt(time) < 0 || parseInt(time) > max) {
					this.throwError(`invalid time`, errorCode);
				}
				max = 60;
			}
		} else if (typeof this.validationSubject.subject !== 'string') {
			this.throwError(`invalid time`, errorCode);
		}
		return this;
	}

	public own(callback: (validationSubject: IValidationSubject) => string | true, errorCode?: ErrorCodes) {
		const validationMessage = callback(this.validationSubject);
		if (validationMessage !== true) {
			this.throwError(validationMessage, errorCode);
		}
		return this;
	}

	public throwError(message: string, errorCode?: ErrorCodes) {
		if (typeof this.error !== 'undefined') {
			throw this.error;
		} else if (errorCode) {
			throw new AppletResourcesError({
				kind: 'appletResourcesError',
				message: `${ErrorCodesMessages[errorCode]} - Validation info: ${message}`,
				code: errorCode,
			});
		} else {
			console.warn(`'${this.validationSubject.name}' parameter validation warning: ${message}`);
		}
	}

	public canBeNullAndIs() {
		return this._canBeNull && this.validationSubject.subject === null;
	}

	private getSubjectFromOutside(validationSubject: IOutsideValidationSubject): IValidationSubject {
		if (Object.keys(validationSubject).length !== 1) {
			this.throwError('has to have exactly one key validated');
		}
		for (const key in validationSubject) {
			if (Object.prototype.hasOwnProperty.call(validationSubject, key)) {
				return {
					name: key,
					subject: validationSubject[key],
				};
			}
		}
		return { name: '', subject: undefined };
	}

	private checkObjectInterface(objectInterface: object, subject: object) {
		const objectValidator = new ValidateObjectInterface();
		const validation = objectValidator.validate(objectInterface, subject);
		if (validation !== true) {
			this.throwError(validation);
		}
	}
}

export default function validate(validationSubject: IOutsideValidationSubject): Validate {
	return new Validate(validationSubject);
}
